import React, { useEffect, useMemo, useState } from 'react';
import {
  ChevronDown,
  FileSpreadsheet,
  FileText,
  FolderKanban,
  HelpCircle,
  LayoutDashboard,
  Settings,
  ShieldCheck,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { formatRoleLabel, getCriteriaScope, isSuperadmin } from '../lib/accessRules';
import {
  fetchKriteriaGroupsForProdiList,
  fetchKriteriaList,
  KriteriaType,
  mapCriteriaForAccess,
} from '../lib/kriteriaMetadata';
import { fetchProfileLookups, formatProdi } from '../lib/profileLookups';
import logoPolimdo from '../assets/logo/logo-polimdo.png';

const mapSidebarCriteria = (criteriaList, prodiId = null) =>
  mapCriteriaForAccess(criteriaList).map((criteria) => ({
    ...criteria,
    route: prodiId ? `${criteria.route}?prodi=${prodiId}` : criteria.route,
    fields:
      criteria.tipe_kriteria === KriteriaType.NARASI
        ? (criteria.fields || []).map((field) => ({
            label: field.label,
            field: field.nama_field,
          }))
        : [],
  }));

function Sidebar({
  profile,
  settings,
  collapsed = false,
  showCriteria = true,
  setShowCriteria,
  activeMenu = 'dashboard',
}) {
  const navigate = useNavigate();
  const [dynamicCriteria, setDynamicCriteria] = useState([]);
  const [prodiCriteriaGroups, setProdiCriteriaGroups] = useState([]);
  const [openProdiGroups, setOpenProdiGroups] = useState({});

  const isAdmin = isSuperadmin(profile);
  const go = (path) => navigate(path);
  const isActive = (menu) => activeMenu === menu;

  const criteriaMenu = useMemo(
    () => mapSidebarCriteria(dynamicCriteria),
    [dynamicCriteria]
  );

  const visibleCriteria = useMemo(
    () => getCriteriaScope(profile, criteriaMenu),
    [criteriaMenu, profile]
  );

  useEffect(() => {
    let mounted = true;

    const loadCriteria = async () => {
      if (!profile) {
        if (mounted) {
          setDynamicCriteria([]);
          setProdiCriteriaGroups([]);
        }
        return;
      }

      if (!isAdmin && !profile?.prodiData?.id) {
        if (mounted) {
          setDynamicCriteria([]);
          setProdiCriteriaGroups([]);
        }
        return;
      }

      try {
        if (isAdmin) {
          const lookups = await fetchProfileLookups(supabase);
          const groups = await fetchKriteriaGroupsForProdiList(
            supabase,
            lookups.prodiList || []
          );

          if (mounted) {
            const nextGroups = groups.map((group) => ({
              prodi: group.prodi,
              criteria: mapSidebarCriteria(group.criteria, group.prodi.id),
            }));

            setDynamicCriteria([]);
            setProdiCriteriaGroups(nextGroups);
            setOpenProdiGroups((prev) => {
              if (Object.keys(prev).length > 0) return prev;
              return nextGroups[0]?.prodi?.id ? { [nextGroups[0].prodi.id]: true } : {};
            });
          }
          return;
        }

        const criteria = await fetchKriteriaList(supabase, {
          prodiId: profile.prodiData.id,
        });

        if (mounted) {
          setDynamicCriteria(criteria);
          setProdiCriteriaGroups([]);
        }
      } catch (error) {
        if (mounted) {
          setDynamicCriteria([]);
          setProdiCriteriaGroups([]);
        }
      }
    };

    loadCriteria();

    return () => {
      mounted = false;
    };
  }, [isAdmin, profile]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const renderCriteriaMenu = () => {
    if (isAdmin) {
      return (
        <>
          {!collapsed &&
            prodiCriteriaGroups.map((group) => {
              const isOpen = Boolean(openProdiGroups[group.prodi.id]);

              return (
                <div className="sidebar-prodi-group" key={group.prodi.id}>
                  <button
                    className={`sidebar-link sidebar-prodi-toggle ${isOpen ? 'open' : ''}`}
                    type="button"
                    onClick={() =>
                      setOpenProdiGroups((prev) => ({
                        ...prev,
                        [group.prodi.id]: !prev[group.prodi.id],
                      }))
                    }
                  >
                    <FileText size={18} />
                    <span>{formatProdi(group.prodi)}</span>
                    <ChevronDown size={16} className="sidebar-chevron" />
                  </button>

                  {isOpen && (
                    <div className="sidebar-submenu">
                      {group.criteria.map((item) => (
                        <button
                          className={`sidebar-sublink ${
                            isActive(`${group.prodi.id}:${item.menu}`) ? 'active' : ''
                          }`}
                          key={`${group.prodi.id}-${item.code}`}
                          onClick={() => go(item.route)}
                          type="button"
                        >
                          {item.label}
                        </button>
                      ))}

                      {group.criteria.length === 0 && (
                        <span className="sidebar-empty-state">Belum ada kriteria</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

          {collapsed && (
            <button className="sidebar-link" type="button" disabled>
              <FileText size={18} />
            </button>
          )}
        </>
      );
    }

    if (visibleCriteria.length === 0) {
      return (
        <button className="sidebar-link" type="button" disabled>
          <FileText size={18} />
          {!collapsed && <span>Memuat akses...</span>}
        </button>
      );
    }

    if (visibleCriteria.length === 1 && visibleCriteria[0].fields?.length > 0) {
      const item = visibleCriteria[0];

      return (
        <>
          <button
            className={`sidebar-link ${showCriteria ? 'open' : ''} ${
              isActive(item.menu) ? 'active' : ''
            }`}
            onClick={() => setShowCriteria((prev) => !prev)}
            type="button"
          >
            <FileText size={18} />
            {!collapsed && (
              <>
                <span>{item.label}</span>
                <ChevronDown size={16} className="sidebar-chevron" />
              </>
            )}
          </button>

          {showCriteria && !collapsed && (
            <div className="sidebar-submenu">
              {item.fields.map((field) => (
                <button
                  className="sidebar-sublink"
                  key={field.field}
                  onClick={() => go(`${item.route}?field=${field.field}`)}
                  type="button"
                >
                  {field.label}
                </button>
              ))}
            </div>
          )}
        </>
      );
    }

    return (
      <>
        <button
          className={`sidebar-link ${showCriteria ? 'open' : ''}`}
          onClick={() => setShowCriteria((prev) => !prev)}
          type="button"
        >
          <FileText size={18} />
          {!collapsed && (
            <>
              <span>Kriteria</span>
              <ChevronDown size={16} className="sidebar-chevron" />
            </>
          )}
        </button>

        {showCriteria && !collapsed && (
          <div className="sidebar-submenu">
            {visibleCriteria.map((item) => (
              <button
                className={`sidebar-sublink ${isActive(item.menu) ? 'active' : ''}`}
                key={item.code}
                onClick={() => go(item.route)}
                type="button"
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
      </>
    );
  };

  return (
    <aside className={`app-sidebar ${collapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-brand">
        <div className="sidebar-brand-logo">
          <img src={logoPolimdo} alt="Logo Politeknik Negeri Manado" />
        </div>

        {!collapsed && (
          <div className="sidebar-brand-text">
            <div className="brand-title">
              {(settings?.nama_sistem || 'Sistem Akreditasi').toUpperCase()}
            </div>
            <div className="brand-subtitle">
              {settings?.nama_kampus || 'Politeknik Negeri Manado'}
            </div>
          </div>
        )}
      </div>

      <div className="sidebar-scroll">
        <div className="sidebar-section">
          {!collapsed && <div className="sidebar-section-title">MENU UTAMA</div>}

          <button
            className={`sidebar-link ${isActive('dashboard') ? 'active' : ''}`}
            onClick={() => go('/dashboard')}
            type="button"
          >
            <LayoutDashboard size={18} />
            {!collapsed && <span>Dashboard</span>}
          </button>
        </div>

        <div className="sidebar-section">
          {!collapsed && <div className="sidebar-section-title">DATA AKREDITASI</div>}
          {renderCriteriaMenu()}
        </div>

        <div className="sidebar-section">
          {!collapsed && <div className="sidebar-section-title">REKAP & ARSIP</div>}

          <button
            className={`sidebar-link ${isActive('arsip') ? 'active' : ''}`}
            onClick={() => go('/arsip')}
            type="button"
          >
            <FolderKanban size={18} />
            {!collapsed && <span>Arsip Data</span>}
          </button>

          <button
            className={`sidebar-link ${isActive('laporan') ? 'active' : ''}`}
            onClick={() => go('/laporan')}
            type="button"
          >
            <FileSpreadsheet size={18} />
            {!collapsed && <span>Laporan</span>}
          </button>

          {isAdmin && (
            <button
              className={`sidebar-link ${isActive('superadmin') ? 'active' : ''}`}
              onClick={() => go('/superadmin')}
              type="button"
            >
              <ShieldCheck size={18} />
              {!collapsed && <span>Superadmin</span>}
            </button>
          )}
        </div>

        <div className="sidebar-section">
          {!collapsed && <div className="sidebar-section-title">LAINNYA</div>}

          <button
            className={`sidebar-link ${isActive('settings') ? 'active' : ''}`}
            onClick={() => go('/settings')}
            type="button"
          >
            <Settings size={18} />
            {!collapsed && <span>Pengaturan</span>}
          </button>

          <button
            className={`sidebar-link ${isActive('bantuan') ? 'active' : ''}`}
            onClick={() => go('/bantuan')}
            type="button"
          >
            <HelpCircle size={18} />
            {!collapsed && <span>Bantuan</span>}
          </button>
        </div>
      </div>

      <div className="sidebar-user-card">
        <div className="sidebar-user-avatar">
          {profile?.nama_lengkap ? profile.nama_lengkap.charAt(0).toUpperCase() : 'U'}
        </div>

        {!collapsed && (
          <div className="sidebar-user-info">
            <div className="sidebar-user-name">{profile?.nama_lengkap || 'Pengguna'}</div>
            <div className="sidebar-user-sub">{formatRoleLabel(profile)}</div>
            <div className="sidebar-user-status">Online</div>
          </div>
        )}
      </div>

      {!collapsed && (
        <button className="sidebar-logout-btn" onClick={handleLogout} type="button">
          Logout
        </button>
      )}
    </aside>
  );
}

export default Sidebar;
